import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  ReferenceLine,
} from "recharts";
import { Wrench, TrendingUp, Copy, Check, AlertCircle, Loader2 } from "lucide-react";
import { api } from "../lib/api";
import useSeo from "../lib/useSeo";

/**
 * IPC Calculator — Chilean CPI historical viewer + monetary value adjuster.
 * Data source: /api/utilities/ipc-series (proxy + cache to mindicador.cl).
 */

const fmtCLP = (n) =>
  new Intl.NumberFormat("es-CL", { style: "currency", currency: "CLP", maximumFractionDigits: 0 }).format(
    Math.round(n || 0),
  );

const groupInt = (n) =>
  new Intl.NumberFormat("es-CL", { maximumFractionDigits: 0 }).format(Math.round(n || 0));

const parseAmount = (raw) => {
  const digits = String(raw ?? "").replace(/[^\d]/g, "");
  return digits ? parseInt(digits, 10) : 0;
};

const MONTHS_ES = [
  "Ene", "Feb", "Mar", "Abr", "May", "Jun",
  "Jul", "Ago", "Sep", "Oct", "Nov", "Dic",
];
const monthLabel = (isoYm) => {
  if (!isoYm) return "";
  const [y, m] = isoYm.split("-");
  const idx = Math.max(0, Math.min(11, parseInt(m, 10) - 1));
  return `${MONTHS_ES[idx]} ${y}`;
};

const formatUpdatedAt = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return new Intl.DateTimeFormat("es-CL", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      timeZone: "America/Santiago",
    }).format(d);
  } catch {
    return iso;
  }
};

const RANGES = [
  { key: "1y", label: "1 año", months: 12 },
  { key: "5y", label: "5 años", months: 60 },
  { key: "10y", label: "10 años", months: 120 },
  { key: "all", label: "Todo", months: null },
];

export default function IpcCalculator() {
  useSeo({
    title: "Calculadora de IPC · Utilidades DoSoft",
    description:
      "Ajusta valores monetarios por IPC en Chile y explora la variación mensual del INE desde 1988 en un gráfico interactivo.",
    canonical: "/utilidades/ipc",
  });

  // noindex — internal tool.
  useEffect(() => {
    let el = document.head.querySelector('meta[name="robots"]');
    const prev = el?.getAttribute("content");
    if (!el) {
      el = document.createElement("meta");
      el.setAttribute("name", "robots");
      document.head.appendChild(el);
    }
    el.setAttribute("content", "noindex,nofollow");
    return () => {
      if (prev) el.setAttribute("content", prev);
      else el.parentNode?.removeChild(el);
    };
  }, []);

  const [series, setSeries] = useState([]);
  const [baseDate, setBaseDate] = useState(null);
  const [sourceLabel, setSourceLabel] = useState("");
  const [sourceUrl, setSourceUrl] = useState(null);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .get("/utilities/ipc-series")
      .then(({ data }) => {
        if (cancelled) return;
        setSeries(Array.isArray(data?.series) ? data.series : []);
        setBaseDate(data?.base || null);
        setSourceLabel(data?.source || "");
        setSourceUrl(data?.source_url || null);
        setUpdatedAt(data?.updated_at || null);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e?.message || "No se pudo cargar la serie de IPC.");
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, []);

  const seriesByDate = useMemo(() => {
    const m = new Map();
    series.forEach((r) => m.set(r.date, r));
    return m;
  }, [series]);

  const lastDate = series.length ? series[series.length - 1].date : null;
  const firstDate = series.length ? series[0].date : null;

  // ---------- Adjuster ----------
  const [amountRaw, setAmountRaw] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  // Default dates once data arrives.
  useEffect(() => {
    if (!series.length) return;
    if (!fromDate) {
      // 12 months back from last
      const idx = Math.max(0, series.length - 13);
      setFromDate(series[idx].date);
    }
    if (!toDate) setToDate(lastDate);
  }, [series]);

  const amount = parseAmount(amountRaw);

  // Official INE-backed adjustment (replaces client-side chained-index math for accuracy).
  const [adjustment, setAdjustment] = useState(null);
  const [adjLoading, setAdjLoading] = useState(false);
  const [adjError, setAdjError] = useState(null);

  useEffect(() => {
    if (!amount || !fromDate || !toDate) {
      setAdjustment(null);
      setAdjError(null);
      return;
    }
    const [fy, fm] = fromDate.split("-");
    const [ty, tm] = toDate.split("-");
    const ctl = new AbortController();
    setAdjLoading(true);
    setAdjError(null);
    const t = setTimeout(() => {
      api
        .get("/utilities/ipc-adjust", {
          params: {
            from_year: parseInt(fy, 10),
            from_month: parseInt(fm, 10),
            to_year: parseInt(ty, 10),
            to_month: parseInt(tm, 10),
            amount,
          },
          signal: ctl.signal,
        })
        .then(({ data }) => {
          setAdjustment({
            adjusted: data.adjusted_value ?? 0,
            adjustedLabel: data.adjusted_value_label ?? "",
            variationPct: data.variation_pct ?? 0,
            variationLabel: data.variation_pct_label ?? "",
            months: data.months ?? "",
            periodLabel: data.period_label ?? "",
          });
        })
        .catch((e) => {
          if (e.name === "CanceledError" || e.name === "AbortError") return;
          setAdjError("No se pudo consultar la calculadora oficial del INE.");
          setAdjustment(null);
        })
        .finally(() => setAdjLoading(false));
    }, 250);
    return () => {
      clearTimeout(t);
      ctl.abort();
    };
  }, [amount, fromDate, toDate]);

  // ---------- Chart ----------
  const [range, setRange] = useState("10y");
  const [metric, setMetric] = useState("variation"); // 'variation' | 'index'

  const chartData = useMemo(() => {
    if (!series.length) return [];
    const cfg = RANGES.find((r) => r.key === range);
    const slice = cfg?.months ? series.slice(-cfg.months) : series;
    return slice.map((r) => ({
      date: r.date,
      variation: r.variation,
      index: r.index,
    }));
  }, [series, range]);

  const latest = series.length ? series[series.length - 1] : null;
  const last12 = useMemo(() => {
    if (series.length < 13) return null;
    const first = series[series.length - 13].index;
    const last = series[series.length - 1].index;
    return ((last / first - 1) * 100);
  }, [series]);

  const copy = async (label, value) => {
    try {
      await navigator.clipboard.writeText(String(Math.round(value)));
      toast.success(`${label} copiado: ${fmtCLP(value)}`);
    } catch {
      toast.error("No se pudo copiar");
    }
  };

  // Options for date selects (year-month).
  const dateOptions = useMemo(() => series.map((r) => r.date), [series]);

  // Build maps: year -> months[] and set of available years
  const { years, monthsByYear } = useMemo(() => {
    const map = new Map();
    for (const d of dateOptions) {
      const [y, m] = d.split("-");
      if (!map.has(y)) map.set(y, []);
      map.get(y).push(m);
    }
    return {
      years: Array.from(map.keys()),
      monthsByYear: map,
    };
  }, [dateOptions]);

  return (
    <div className="bg-ash-100 min-h-[70vh]">
      <main className="max-w-6xl mx-auto px-6 py-8 md:py-12">
        {/* Header */}
        <div className="mb-6">
          <div className="text-xs uppercase tracking-widest text-brand-slate font-mono mb-1 flex items-center gap-2">
            <Wrench size={12} strokeWidth={1.8} /> Herramientas DoSoft
          </div>
          <h1 className="font-display font-black text-3xl md:text-5xl tracking-tight text-ink-900 flex items-center gap-3 flex-wrap">
            Calculadora de IPC
            <span className="text-xs md:text-sm font-mono font-normal bg-brand/15 text-brand-active px-2 py-1 rounded uppercase tracking-widest">
              Chile · INE
            </span>
          </h1>
          <p className="text-ink-800/70 text-sm md:text-base mt-3">
            Ajusta valores monetarios entre dos fechas y explora la variación histórica del Índice de Precios al Consumidor.
          </p>
          {!loading && !error && series.length > 0 && (
            <div
              data-testid="ipc-source-badge"
              className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] font-mono text-ink-800/60"
            >
              <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-brand/10 text-brand-active">
                <span className="w-1.5 h-1.5 rounded-full bg-[#0E6B2A] animate-pulse" />
                EN VIVO
              </span>
              <span>
                Fuente:{" "}
                {sourceUrl ? (
                  <a
                    href={sourceUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline hover:text-brand"
                  >
                    {sourceLabel}
                  </a>
                ) : (
                  sourceLabel
                )}
              </span>
            </div>
          )}
        </div>

        {loading && (
          <div className="flex items-center gap-3 p-6 rounded-2xl bg-white border border-ash-100/80 text-ink-800/70">
            <Loader2 className="animate-spin" size={18} /> Cargando serie histórica desde el Banco Central de Chile…
          </div>
        )}

        {error && !loading && (
          <div
            data-testid="ipc-error"
            className="flex items-start gap-3 p-5 rounded-2xl bg-white border border-red-200 text-red-700"
          >
            <AlertCircle size={18} className="mt-0.5 shrink-0" />
            <div>
              <div className="font-semibold text-sm">No se pudo cargar la serie del IPC</div>
              <div className="text-xs opacity-80 mt-1">
                {error} · La fuente oficial puede estar temporalmente fuera de servicio. Intenta más tarde.
              </div>
            </div>
          </div>
        )}

        {!loading && !error && series.length > 0 && (
          <>
            {/* KPIs */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
              <Kpi
                label="Último dato"
                value={monthLabel(latest?.date)}
                sublabel="mes reportado"
              />
              <Kpi
                label="Variación mensual"
                value={`${latest?.variation > 0 ? "+" : ""}${(latest?.variation ?? 0).toFixed(2)}%`}
                sublabel={monthLabel(latest?.date)}
                accent={latest?.variation >= 0 ? "up" : "down"}
              />
              <Kpi
                label="IPC 12 meses"
                value={last12 != null ? `${last12 > 0 ? "+" : ""}${last12.toFixed(2)}%` : "—"}
                sublabel="acumulado"
                accent={last12 != null && last12 >= 0 ? "up" : "down"}
              />
              <Kpi
                label="Serie disponible"
                value={`${monthLabel(firstDate)} → ${monthLabel(lastDate)}`}
                sublabel={`${series.length} meses`}
              />
            </div>

            {/* Adjuster */}
            <section className="rounded-2xl overflow-hidden border border-ink-500 bg-white shadow-lg mb-6">
              <div className="bg-ink-900 text-ash-100 px-5 py-4">
                <div className="text-[10px] uppercase tracking-widest font-mono text-ash-500 mb-1">
                  Ajustador por IPC
                </div>
                <div className="text-sm text-ash-100/90">
                  ¿Cuánto vale hoy un monto de otra época? Ingresa monto y fechas.
                </div>
              </div>

              <div className="p-6 md:p-8 grid grid-cols-1 md:grid-cols-3 gap-4">
                <label className="block md:col-span-3">
                  <div className="text-[11px] uppercase tracking-widest font-mono text-ink-800/60 mb-2">
                    Monto original (CLP)
                  </div>
                  <div className="flex items-center border-2 border-brand rounded-xl px-5 py-4 focus-within:border-brand-active transition-colors">
                    <span className="font-display font-black text-3xl md:text-4xl text-brand mr-3">$</span>
                    <input
                      data-testid="ipc-amount-input"
                      type="text"
                      inputMode="numeric"
                      autoFocus
                      placeholder="0"
                      value={amountRaw ? groupInt(parseAmount(amountRaw)) : ""}
                      onChange={(e) => setAmountRaw(e.target.value)}
                      className="w-full font-display font-black text-3xl md:text-4xl text-ink-900 bg-transparent outline-none placeholder:text-ash-500/40"
                    />
                  </div>
                </label>

                <YearMonthPicker
                  label="Fecha origen"
                  value={fromDate}
                  onChange={setFromDate}
                  years={years}
                  monthsByYear={monthsByYear}
                  testidYear="ipc-from-year"
                  testidMonth="ipc-from-month"
                />
                <YearMonthPicker
                  label="Fecha destino"
                  value={toDate}
                  onChange={setToDate}
                  years={years}
                  monthsByYear={monthsByYear}
                  testidYear="ipc-to-year"
                  testidMonth="ipc-to-month"
                />
                <div className="flex items-end">
                  <button
                    data-testid="ipc-set-latest"
                    onClick={() => lastDate && setToDate(lastDate)}
                    className="w-full h-11 border border-ink-500/40 hover:border-brand hover:text-brand text-ink-800 rounded-lg text-xs uppercase tracking-widest font-mono transition-colors"
                    title="Actualizar destino al último dato disponible"
                  >
                    Ir al último mes
                  </button>
                </div>
              </div>

              {/* Results */}
              <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-ash-100/70 border-t border-ash-100/70">
                <ResultCell
                  label="Monto ajustado"
                  sublabel={`en ${monthLabel(toDate)}`}
                  value={adjustment ? adjustment.adjusted : 0}
                  onCopy={() => adjustment && copy("Monto ajustado", adjustment.adjusted)}
                  testid="ipc-result-adjusted"
                  highlight
                  loading={adjLoading}
                />
                <InfoCell
                  label="Variación IPC"
                  sublabel={`${monthLabel(fromDate)} → ${monthLabel(toDate)}`}
                  value={
                    adjustment
                      ? adjustment.variationLabel ||
                        `${adjustment.variationPct >= 0 ? "+" : ""}${adjustment.variationPct.toFixed(1)}%`
                      : "—"
                  }
                  testid="ipc-result-variation"
                  accent={adjustment && adjustment.variationPct >= 0 ? "up" : "down"}
                  loading={adjLoading}
                />
                <InfoCell
                  label="Período"
                  sublabel="meses transcurridos"
                  value={adjustment ? adjustment.months || "—" : "—"}
                  testid="ipc-result-months"
                  loading={adjLoading}
                />
              </div>
              {adjError && (
                <div className="px-6 py-3 bg-red-50 text-red-700 text-xs border-t border-red-100">
                  {adjError}
                </div>
              )}
              {adjustment && (
                <div className="px-6 py-2 bg-ash-100/40 text-[10px] font-mono text-ink-800/50 border-t border-ash-100/60">
                  Cálculo oficial · Instituto Nacional de Estadísticas (INE) ·{" "}
                  <a
                    href="https://calculadoraipc.ine.cl"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline hover:text-brand"
                  >
                    calculadoraipc.ine.cl
                  </a>
                </div>
              )}
            </section>

            {/* Chart */}
            <section className="rounded-2xl overflow-hidden border border-ash-100/80 bg-white shadow-sm">
              <div className="px-5 py-4 border-b border-ash-100/70 flex flex-wrap items-center gap-3 justify-between">
                <div className="flex items-center gap-2">
                  <TrendingUp size={16} className="text-brand" strokeWidth={2} />
                  <h2 className="font-display font-bold text-ink-900">Serie histórica</h2>
                </div>
                <div className="flex gap-1 flex-wrap">
                  {/* Metric */}
                  <div className="flex rounded-lg bg-ash-100 p-0.5 mr-2">
                    <ChartToggle
                      active={metric === "variation"}
                      onClick={() => setMetric("variation")}
                      testid="ipc-metric-variation"
                    >
                      Variación %
                    </ChartToggle>
                    <ChartToggle
                      active={metric === "index"}
                      onClick={() => setMetric("index")}
                      testid="ipc-metric-index"
                    >
                      Índice base
                    </ChartToggle>
                  </div>
                  {/* Range */}
                  <div className="flex rounded-lg bg-ash-100 p-0.5">
                    {RANGES.map((r) => (
                      <ChartToggle
                        key={r.key}
                        active={range === r.key}
                        onClick={() => setRange(r.key)}
                        testid={`ipc-range-${r.key}`}
                      >
                        {r.label}
                      </ChartToggle>
                    ))}
                  </div>
                </div>
              </div>

              <div className="p-3 md:p-5">
                <div style={{ width: "100%", height: 340 }}>
                  <ResponsiveContainer>
                    <LineChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                      <XAxis
                        dataKey="date"
                        tickFormatter={(d) => {
                          const [y, m] = d.split("-");
                          return range === "1y" ? `${MONTHS_ES[parseInt(m, 10) - 1]}` : y;
                        }}
                        minTickGap={30}
                        stroke="#6B7280"
                        fontSize={11}
                      />
                      <YAxis
                        stroke="#6B7280"
                        fontSize={11}
                        tickFormatter={(v) =>
                          metric === "variation" ? `${v.toFixed(1)}%` : v.toFixed(0)
                        }
                      />
                      <Tooltip
                        content={<CustomTooltip metric={metric} />}
                        cursor={{ stroke: "#8DDA23", strokeWidth: 1 }}
                      />
                      {metric === "variation" && (
                        <ReferenceLine y={0} stroke="#9CA3AF" strokeDasharray="3 3" />
                      )}
                      <Line
                        type="monotone"
                        dataKey={metric}
                        stroke="#8DDA23"
                        strokeWidth={2}
                        dot={false}
                        activeDot={{ r: 4, fill: "#0E6B2A" }}
                        isAnimationActive={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="px-5 pb-4 text-[11px] text-ink-800/50 font-mono">
                Fuente: {sourceLabel || "—"} · Base del índice reconstruido = 100 en {monthLabel(baseDate)}
                {updatedAt && ` · Actualizado ${formatUpdatedAt(updatedAt)}`}.
              </div>
            </section>

            <div className="mt-8 text-xs text-ink-800/50 whitespace-nowrap overflow-x-auto">
              <strong>Nota:</strong> El ajuste multiplica el monto por el cociente entre los índices IPC del mes destino y el mes origen. Los datos se actualizan cada 6 horas desde la fuente pública.
            </div>
          </>
        )}
      </main>
    </div>
  );
}

/* ------------------------ Sub-components ------------------------ */

function Kpi({ label, value, sublabel, accent }) {
  const color =
    accent === "up" ? "text-[#0E6B2A]" : accent === "down" ? "text-red-600" : "text-ink-900";
  return (
    <div className="bg-white rounded-xl border border-ash-100/80 p-4">
      <div className="text-[10px] uppercase tracking-widest font-mono text-ink-800/60 mb-1">
        {label}
      </div>
      <div className={`font-display font-black text-lg md:text-xl leading-tight ${color}`}>
        {value}
      </div>
      {sublabel && (
        <div className="text-[11px] text-ink-800/50 font-mono mt-1 truncate">{sublabel}</div>
      )}
    </div>
  );
}

function YearMonthPicker({ label, value, onChange, years, monthsByYear, testidYear, testidMonth }) {
  const [y, m] = value ? value.split("-") : ["", ""];
  const availableMonths = monthsByYear.get(y) || [];

  const setYear = (newY) => {
    const months = monthsByYear.get(newY) || [];
    // Preserve month if available in new year, else pick the closest (fallback to last).
    let newM = m && months.includes(m) ? m : months[months.length - 1] || "01";
    onChange(`${newY}-${newM}`);
  };
  const setMonth = (newM) => {
    onChange(`${y}-${newM}`);
  };

  return (
    <div>
      <div className="text-[11px] uppercase tracking-widest font-mono text-ink-800/60 mb-2">
        {label}
      </div>
      <div className="grid grid-cols-2 gap-2">
        <select
          data-testid={testidMonth}
          value={m || ""}
          onChange={(e) => setMonth(e.target.value)}
          className="h-11 border border-ink-500/30 hover:border-brand focus:border-brand focus:outline-none rounded-lg px-2 bg-white text-sm text-ink-900 font-mono"
          aria-label={`${label} · mes`}
        >
          {availableMonths.map((mm) => (
            <option key={mm} value={mm}>
              {MONTHS_ES[parseInt(mm, 10) - 1]}
            </option>
          ))}
        </select>
        <select
          data-testid={testidYear}
          value={y || ""}
          onChange={(e) => setYear(e.target.value)}
          className="h-11 border border-ink-500/30 hover:border-brand focus:border-brand focus:outline-none rounded-lg px-2 bg-white text-sm text-ink-900 font-mono"
          aria-label={`${label} · año`}
        >
          {years.map((yy) => (
            <option key={yy} value={yy}>
              {yy}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}

function ResultCell({ label, sublabel, value, onCopy, testid, highlight, loading }) {
  const [copied, setCopied] = useState(false);
  const handle = () => {
    onCopy();
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <button
      data-testid={testid}
      onClick={handle}
      disabled={loading}
      className={`text-left p-5 md:p-6 hover:bg-ash-100/40 transition-colors group ${
        highlight ? "bg-brand/5" : ""
      } ${loading ? "opacity-60" : ""}`}
      title="Click para copiar"
    >
      <div className="flex items-center gap-2 mb-2">
        <span className="text-[10px] uppercase tracking-widest font-mono text-ink-800/80 font-bold">
          {label}
        </span>
        {sublabel && (
          <span className="text-[10px] font-mono text-ink-800/50 truncate">({sublabel})</span>
        )}
        <span className="ml-auto opacity-0 group-hover:opacity-100 transition-opacity">
          {copied ? (
            <Check size={12} className="text-[#0E6B2A]" strokeWidth={3} />
          ) : (
            <Copy size={12} className="text-ash-500" strokeWidth={1.8} />
          )}
        </span>
      </div>
      <div
        className={`font-display font-black text-2xl md:text-3xl leading-none ${
          value > 0 ? "text-ink-900" : "text-ash-500/60"
        }`}
      >
        {loading ? (
          <span className="inline-flex items-center gap-2 text-ash-500/80 text-lg">
            <Loader2 size={18} className="animate-spin" /> Consultando INE…
          </span>
        ) : (
          fmtCLP(value)
        )}
      </div>
    </button>
  );
}

function InfoCell({ label, sublabel, value, testid, accent, loading }) {
  const color =
    accent === "up" ? "text-[#0E6B2A]" : accent === "down" ? "text-red-600" : "text-ink-900";
  return (
    <div data-testid={testid} className={`p-5 md:p-6 ${loading ? "opacity-60" : ""}`}>
      <div className="flex items-center gap-2 mb-2">
        <span className="text-[10px] uppercase tracking-widest font-mono text-ink-800/80 font-bold">
          {label}
        </span>
        {sublabel && (
          <span className="text-[10px] font-mono text-ink-800/50 truncate">({sublabel})</span>
        )}
      </div>
      <div className={`font-display font-black text-2xl md:text-3xl leading-none ${color}`}>
        {loading ? <span className="text-ash-500/60">—</span> : value}
      </div>
    </div>
  );
}

function ChartToggle({ active, onClick, testid, children }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`px-3 h-8 rounded-md text-[11px] uppercase tracking-widest font-mono transition-all ${
        active
          ? "bg-white text-ink-900 shadow-sm font-semibold"
          : "text-ink-800/60 hover:text-ink-900"
      }`}
    >
      {children}
    </button>
  );
}

function CustomTooltip({ active, payload, metric }) {
  if (!active || !payload || !payload.length) return null;
  const row = payload[0].payload;
  return (
    <div className="bg-ink-900 text-ash-100 text-xs rounded-md px-3 py-2 shadow-lg border border-ink-500">
      <div className="font-mono text-[10px] uppercase tracking-widest text-ash-500 mb-1">
        {monthLabel(row.date)}
      </div>
      {metric === "variation" ? (
        <div>
          Variación:{" "}
          <span className="font-bold text-brand">
            {row.variation > 0 ? "+" : ""}
            {row.variation.toFixed(2)}%
          </span>
        </div>
      ) : (
        <div>
          Índice:{" "}
          <span className="font-bold text-brand">{row.index.toFixed(2)}</span>
        </div>
      )}
    </div>
  );
}
